/** Zeitstempel des Backends lesen und in deutscher Ortszeit anzeigen.
 *
 *  **Warum das eine eigene Stelle braucht.** Die Backend-Container laufen ohne
 *  gesetztes `TZ`, also in UTC, und schreiben Zeitstempel *naiv* — ohne
 *  Zonenangabe. Auf dem Weg ins Frontend nehmen sie zwei verschiedene Formen:
 *
 *  * als `datetime`-Spalte über connexion, das ein `Z` anhängt — korrekt
 *  * als String in einem JSON-Blob, **ohne** `Z` — JavaScript liest das als
 *    Ortszeit, und die Anzeige liegt um den Zonenversatz zu früh
 *
 *  Der zweite Fall war der QS-Befund zur Handelsregister-Nachverfolgung
 *  (qs/dino-qs#24), der erste der zur Download-Übersicht (#16). Beide Formen
 *  werden hier als UTC gelesen.
 *
 *  **Ein reines Datum ist kein Zeitstempel.** `2026-08-23` bleibt lokale
 *  Mitternacht. Als UTC gelesen würde daraus in Deutschland der Vortag 22:00 —
 *  und damit unter Umständen ein falscher Tag in der Anzeige.
 *
 *  Angezeigt wird fest in `Europe/Berlin`, nicht nach der Einstellung des
 *  Betrachters: ein Prüf- oder Download-Zeitpunkt muss belastbar sein.
 */

const DISPLAY_TIME_ZONE = 'Europe/Berlin';

/** Platzhalter, wenn kein Wert vorliegt. */
export const NO_TIMESTAMP = '—';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/;

/** Backend-Wert in einen Date-Wert übersetzen. `null`, wenn nichts Lesbares. */
export const parseBackendTimestamp = (
  input: string | Date | null | undefined
): Date | null => {
  if (!input) return null;

  if (input instanceof Date) {
    return isNaN(input.getTime()) ? null : input;
  }

  const value = String(input).trim();
  if (!value) return null;

  // Reines Datum: lokale Mitternacht, bewusst nicht UTC.
  if (DATE_ONLY.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  let isoValue = value.replace(' ', 'T');
  if (!HAS_ZONE.test(isoValue)) {
    isoValue = `${isoValue}Z`;
  }

  const parsed = new Date(isoValue);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const germanDate = (value: Date) =>
  new Intl.DateTimeFormat('de-DE', {
    timeZone: DISPLAY_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(value);

/** `23.08.2026, 19:44:00` — Datum und Uhrzeit in deutscher Ortszeit. */
export const formatBackendDateTime = (
  input: string | Date | null | undefined,
  options: { withSeconds?: boolean } = {}
): string => {
  const parsed = parseBackendTimestamp(input);
  if (!parsed) return NO_TIMESTAMP;

  const time = new Intl.DateTimeFormat('de-DE', {
    timeZone: DISPLAY_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    ...(options.withSeconds === false ? {} : { second: '2-digit' }),
    hour12: false,
  }).format(parsed);

  return `${germanDate(parsed)}, ${time}`;
};

/** `23.08.2026` — nur das Datum, in deutscher Ortszeit. */
export const formatBackendDate = (
  input: string | Date | null | undefined
): string => {
  const parsed = parseBackendTimestamp(input);
  if (!parsed) return NO_TIMESTAMP;
  return germanDate(parsed);
};
